import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import { RateLimitedBulkActionExecutor } from "@/lib/hooks/api/shared/bulk-filter-types";
import { getFilteredTicketIds } from "./ticket-bulk-filter-utils";
import type {
  BulkActionProgressItem,
  BulkActionProgress,
  FilterBasedBulkActionParams,
} from "@/lib/hooks/api/shared/bulk-filter-types";
import type { TicketFilterCriteria } from "./ticket-bulk-filter-utils";

export function useBulkUpdateTicketStatusByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      status,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<TicketFilterCriteria> & { status: string }) => {
      const tickets = await getFilteredTicketIds(filterCriteria);

      if (tickets.length === 0) {
        throw new Error("No tickets found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = tickets.map((ticket) => ({
        id: ticket.id,
        name: ticket.title || `Ticket ${ticket.ticket_number || ticket.id}`,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: tickets.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === tickets.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        tickets,
        async (ticket: { id: string; title: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase
              .from("tickets")
              .update({
                status,
                updated_at: new Date().toISOString(),
              })
              .eq("id", ticket.id);

            if (error) {
              throw new Error(error.message);
            }

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        },
        () => { updateProgress(); }
      );

      return {
        totalProcessed: tickets.length,
        successful: completedCount,
        failed: errorCount,
        tickets: tickets.map((t) => t.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully updated ${result.successful} tickets`);
      } else {
        toast.warning(`Updated ${result.successful} tickets, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to update ticket status");
      console.error("Error bulk updating ticket status by filter:", error);
    },
  });
}

export function useBulkUpdateTicketAssignmentByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      assignedTo,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<TicketFilterCriteria> & { assignedTo: string | null }) => {
      const tickets = await getFilteredTicketIds(filterCriteria);

      if (tickets.length === 0) {
        throw new Error("No tickets found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = tickets.map((ticket) => ({
        id: ticket.id,
        name: ticket.title || `Ticket ${ticket.ticket_number || ticket.id}`,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: tickets.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === tickets.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        tickets,
        async (ticket: { id: string; title: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase
              .from("tickets")
              .update({
                assigned_to: assignedTo,
                updated_at: new Date().toISOString(),
              })
              .eq("id", ticket.id);

            if (error) {
              throw new Error(error.message);
            }

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        }
      );

      return {
        totalProcessed: tickets.length,
        successful: completedCount,
        failed: errorCount,
        tickets: tickets.map((t) => t.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["tickets"] });
      void queryClient.invalidateQueries({ queryKey: ["tickets-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully assigned ${result.successful} tickets`);
      } else {
        toast.warning(`Assigned ${result.successful} tickets, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to assign tickets");
      console.error("Error bulk assigning tickets by filter:", error);
    },
  });
}
